// src/components/StaffForm.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography, Container, MenuItem, ListItemText, Button,
  Box, TextField, Select, Checkbox, Switch, Grid, Chip,
  Paper, IconButton, Avatar, Divider, InputAdornment,
  Fade, Stack
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import BadgeIcon from "@mui/icons-material/Badge";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import SecurityIcon from "@mui/icons-material/Security";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import AppHeader from './AppHeader';

const StaffForm = ({ setIsLoggedIn }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formValid, setFormValid] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswordReset, setShowPasswordReset] = useState(false);

  const [locations, setLocations] = useState([]);
  const [managerUsers, setManagerUsers] = useState([]);
  const [hrUsers, setHrUsers] = useState([]);

  const [staff, setStaff] = useState({
    full_name: '', staff_code: '', department: '', cessation_date: '',
    allowed_locations: [], username: '', email: '', role: 'staff',
    password: '', is_active: 1, work_type: 'office', ot_eligible: 1,
    ot_rate_profile: null, auto_approve_manual_attendance: 0,
    is_photo_required: 1,
    level1_approver_id: '', level2_approver_id: '',
  });

  useEffect(() => {
    const tenantHeader = { 'X-Tenant': localStorage.getItem('tenant') };
    if (id) {
      axios.get(`${process.env.REACT_APP_STAFF_API_URL}/staff/${id}`, { withCredentials: true, headers: tenantHeader })
        .then(res => setStaff(prev => ({ ...prev, ...res.data })))
        .catch(console.error);
    }

    axios.get(`${process.env.REACT_APP_LOCATION_API_URL}/locations`, { withCredentials: true, headers: tenantHeader })
      .then(res => setLocations(Array.isArray(res.data?.rows) ? res.data.rows : (Array.isArray(res.data) ? res.data : [])))
      .catch(console.error);

    axios.get(`${process.env.REACT_APP_API_URL1}/users/users?role=manager`, { withCredentials: true, headers: tenantHeader })
      .then(res => setManagerUsers(Array.isArray(res.data) ? res.data : []))
      .catch(() => setManagerUsers([]));

    axios.get(`${process.env.REACT_APP_API_URL1}/users/users?role=hr`, { withCredentials: true, headers: tenantHeader })
      .then(res => setHrUsers(Array.isArray(res.data) ? res.data : []))
      .catch(() => setHrUsers([]));
  }, [id]);

  useEffect(() => {
    if (id && staff.allowed_locations.length > 0 && locations.length > 0) {
      const firstItem = staff.allowed_locations[0];
      if (typeof firstItem === 'string') {
        const ids = staff.allowed_locations.map(name => {
          const found = locations.find(l => l.location_name === name);
          return found ? found.id : null;
        }).filter(val => val !== null);
        setStaff(prev => ({ ...prev, allowed_locations: ids }));
      }
    }
  }, [locations, id, staff.allowed_locations]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setStaff((prev) => ({ ...prev, [name]: value }));
    validate({ ...staff, [name]: value });
  };

  const validate = (data = staff) => {
    let tempErrors = {};
    if (!data.full_name?.trim()) tempErrors.full_name = "Required";
    if (!data.staff_code?.trim()) tempErrors.staff_code = "Required";
    if (!data.username?.trim()) tempErrors.username = "Required";
    if (!data.email || !/\S+@\S+\.\S+/.test(data.email)) tempErrors.email = "Invalid";
    if (!id || showPasswordReset) {
      if (!id && !data.password) tempErrors.password = "Required";
      if (data.password && data.password !== confirmPassword) tempErrors.confirmPassword = "Mismatch";
    }
    setErrors(tempErrors);
    setFormValid(Object.keys(tempErrors).length === 0);
    return Object.keys(tempErrors).length === 0;
  };

  useEffect(() => {
    validate();
  }, [confirmPassword, showPasswordReset, staff]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      const payload = { ...staff, cessation_date: staff.cessation_date || null };
      const axiosConfig = { withCredentials: true, headers: { 'X-Tenant': localStorage.getItem('tenant') } };
      if (id) {
        await axios.put(`${process.env.REACT_APP_STAFF_API_URL}/staff/${id}`, payload, axiosConfig);
      } else {
        await axios.post(`${process.env.REACT_APP_STAFF_API_URL}/register-staff`, payload, axiosConfig);
      }
      alert('Staff Saved!');
      navigate('/staff');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsLoggedIn(false);
    navigate('/login');
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f1f3f1', overflowY: 'auto' }}>
      <AppHeader onLogout={handleLogout} />

      <Container maxWidth="xl" sx={{ flexGrow: 1, py: 2, display: 'flex', flexDirection: 'column' }}>
        <Fade in timeout={500}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', paddingBottom: 40 }}>
            {/* Compact Action Bar - Sticky */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
              position: 'sticky',
              top: 0,
              zIndex: 10,
              bgcolor: '#f1f3f1',
              py: 1,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton onClick={() => navigate('/staff')} size="small" sx={{ bgcolor: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Typography variant="h5" sx={{ fontWeight: 900, color: '#1b5e20', letterSpacing: '-0.5px' }}>
                  {id ? 'Update Staff Profile' : 'Staff Registration'}
                </Typography>
              </Box>

              <Stack direction="row" spacing={2}>
                <Button variant="outlined" size="small" onClick={() => navigate('/staff')} sx={{ borderRadius: '10px', px: 3, fontWeight: 700, color: '#2e7d32', borderColor: '#2e7d32' }}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" size="small" disabled={!formValid} sx={{ borderRadius: '10px', bgcolor: '#2e7d32', px: 4, fontWeight: 700, boxShadow: '0 4px 12px rgba(46,125,50,0.2)' }}>
                  {id ? 'Save Changes' : 'Register Now'}
                </Button>
              </Stack>
            </Box>

            {/* Columns with natural alignment */}
            <Grid container spacing={2} sx={{ flexGrow: 0, alignItems: 'flex-start' }}>
              {/* Section 1: Personal (Dense) */}
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2.5, borderRadius: '20px', border: '1px solid #e0e0e0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Avatar sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', width: 36, height: 36 }}><PersonIcon /></Avatar>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Personal Info</Typography>
                  </Box>

                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', ml: 0.5 }}>FULL NAME</Typography>
                      <TextField name="full_name" size="small" fullWidth value={staff.full_name} onChange={handleChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} error={!!errors.full_name} />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', ml: 0.5 }}>STAFF CODE</Typography>
                      <TextField name="staff_code" size="small" fullWidth value={staff.staff_code} onChange={handleChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} error={!!errors.staff_code} />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', ml: 0.5 }}>DEPARTMENT</Typography>
                      <TextField name="department" size="small" fullWidth value={staff.department} onChange={handleChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', ml: 0.5 }}>CESSATION DATE</Typography>
                      <TextField type="date" name="cessation_date" size="small" fullWidth value={staff.cessation_date || ''} onChange={handleChange} InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', ml: 0.5 }}>ROLE</Typography>
                      <Select name="role" fullWidth size="small" value={staff.role} onChange={handleChange} sx={{ borderRadius: '10px' }}>
                        <MenuItem value="staff">Staff</MenuItem>
                        <MenuItem value="manager">Manager</MenuItem>
                        <MenuItem value="hr">HR</MenuItem>
                      </Select>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', ml: 0.5 }}>STATUS</Typography>
                      <Select name="is_active" fullWidth size="small" value={staff.is_active} onChange={handleChange} sx={{ borderRadius: '10px' }}>
                        <MenuItem value={1}>Active</MenuItem>
                        <MenuItem value={0}>Disabled</MenuItem>
                      </Select>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Section 2: Account (Dense) */}
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2.5, borderRadius: '20px', border: '1px solid #e0e0e0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Avatar sx={{ bgcolor: '#fff3e0', color: '#ef6c00', width: 36, height: 36 }}><VerifiedUserIcon /></Avatar>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Account Access</Typography>
                  </Box>

                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', ml: 0.5 }}>USERNAME</Typography>
                      <TextField name="username" size="small" fullWidth value={staff.username} onChange={handleChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', ml: 0.5 }}>EMAIL ADDRESS</Typography>
                      <TextField name="email" size="small" fullWidth value={staff.email} onChange={handleChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                    </Grid>

                    {(!id || showPasswordReset) ? (
                      <>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', ml: 0.5 }}>
                            {id ? 'NEW PASSWORD' : 'PASSWORD'}
                          </Typography>
                          <TextField name="password" type="password" size="small" fullWidth value={staff.password || ''} onChange={handleChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', ml: 0.5 }}>CONFIRM</Typography>
                          <TextField type="password" size="small" fullWidth value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                        </Grid>
                        {id && (
                          <Grid item xs={12}>
                            <Button
                              size="small"
                              variant="text"
                              color="secondary"
                              onClick={() => {
                                setShowPasswordReset(false);
                                setStaff(prev => ({ ...prev, password: '' }));
                                setConfirmPassword('');
                              }}
                              sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                            >
                              Cancel Password Reset
                            </Button>
                          </Grid>
                        )}
                      </>
                    ) : (
                      <Grid item xs={12}>
                        <Box sx={{ p: 2, bgcolor: '#f8f9fa', border: '1px dashed #cbd5e0', borderRadius: '12px', textAlign: 'center' }}>
                          <Typography variant="caption" fontWeight={800} color="text.secondary" display="block" sx={{ mb: 1 }}>
                            SECURITY: PASSWORD ENCRYPTED
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setShowPasswordReset(true)}
                            sx={{ borderRadius: '8px', fontWeight: 700, color: '#d32f2f', borderColor: '#d32f2f' }}
                          >
                            Reset Account Password
                          </Button>
                        </Box>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: '#ef6c00', mb: 1, display: 'block' }}>ALLOWED LOCATIONS</Typography>
                      <Select
                        multiple fullWidth size="small"
                        value={staff.allowed_locations}
                        onChange={(e) => setStaff({ ...staff, allowed_locations: e.target.value })}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((val) => {
                              const loc = locations.find(l => l.id === val);
                              return <Chip key={val} label={loc ? loc.location_name : val} size="small" sx={{ height: 22, fontWeight: 700, fontSize: '0.65rem' }} />;
                            })}
                          </Box>
                        )}
                        sx={{ borderRadius: '10px' }}
                      >
                        {locations.map((loc) => (
                          <MenuItem key={loc.id} value={loc.id}>
                            <Checkbox size="small" checked={staff.allowed_locations.includes(loc.id)} color="success" />
                            <ListItemText primary={<Typography variant="body2">{loc.location_name}</Typography>} />
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Section 3: Policy & Approvers (Dense) */}
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2.5, borderRadius: '20px', border: '1px solid #e0e0e0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Avatar sx={{ bgcolor: '#e3f2fd', color: '#1565c0', width: 36, height: 36 }}><BusinessCenterIcon /></Avatar>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Policies & Routing</Typography>
                  </Box>

                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.5, display: 'block' }}>WORK TYPE & POLICY</Typography>
                      <Stack direction="row" spacing={1}>
                        <Select name="work_type" fullWidth size="small" value={staff.work_type} onChange={handleChange} sx={{ borderRadius: '10px' }}>
                          <MenuItem value="office">Office Based</MenuItem>
                          <MenuItem value="shift_based">Shift Based</MenuItem>
                        </Select>
                        <Box sx={{ px: 2, display: 'flex', alignItems: 'center', bgcolor: '#f5f5f5', borderRadius: '10px', border: '1px solid #ddd' }}>
                          <Typography variant="caption" fontWeight={800} sx={{ mr: 1 }}>OT</Typography>
                          <Switch size="small" checked={staff.ot_eligible === 1} onChange={(e) => setStaff(prev => ({ ...prev, ot_eligible: e.target.checked ? 1 : 0 }))} color="success" />
                        </Box>
                      </Stack>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, bgcolor: '#f1f8e9', borderRadius: '10px', border: '1px solid #c8e6c9' }}>
                      <Typography variant="body2" fontWeight={800} color="#2e7d32">Auto-Approve Requests</Typography>
                      <Switch size="small" checked={staff.auto_approve_manual_attendance === 1} onChange={(e) => setStaff(prev => ({ ...prev, auto_approve_manual_attendance: e.target.checked ? 1 : 0 }))} color="success" />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, bgcolor: '#fff3e0', borderRadius: '10px', border: '1px solid #ffe0b2' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CameraAltIcon sx={{ color: '#ef6c00', fontSize: 18 }} />
                        <Typography variant="body2" fontWeight={800} color="#e65100">Photo Required</Typography>
                      </Box>
                      <Switch size="small" checked={staff.is_photo_required === 1} onChange={(e) => setStaff(prev => ({ ...prev, is_photo_required: e.target.checked ? 1 : 0 }))} color="warning" />
                    </Box>

                    <Divider />

                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <SecurityIcon sx={{ color: '#7b1fa2', fontSize: 18 }} />
                        <Typography variant="caption" fontWeight={800}>APPROVAL WORKFLOW</Typography>
                      </Box>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>LEVEL 1 MANAGER</Typography>
                          <Select fullWidth size="small" value={staff.level1_approver_id ?? ''} onChange={(e) => setStaff(prev => ({ ...prev, level1_approver_id: e.target.value }))} sx={{ borderRadius: '10px' }} displayEmpty>
                            <MenuItem value="">Unassigned</MenuItem>
                            {managerUsers.map(u => <MenuItem key={u.id} value={u.id}><Typography variant="body2" fontWeight={600}>{u.username}</Typography></MenuItem>)}
                          </Select>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>LEVEL 2 HR</Typography>
                          <Select fullWidth size="small" value={staff.level2_approver_id ?? ''} onChange={(e) => setStaff(prev => ({ ...prev, level2_approver_id: e.target.value }))} sx={{ borderRadius: '10px' }} displayEmpty>
                            <MenuItem value="">Unassigned</MenuItem>
                            {hrUsers.map(u => <MenuItem key={u.id} value={u.id}><Typography variant="body2" fontWeight={600}>{u.username}</Typography></MenuItem>)}
                          </Select>
                        </Grid>
                      </Grid>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </form>
        </Fade>
      </Container>
    </Box>
  );
};

export default StaffForm;